import { uuidv4 } from '~/lib/uuid';
import { VisionRunner } from '~/lib/vision/runner';
import Bindings from '../lib/bindings';
import { deps } from '../lib/vision/deps';
import { VisionUpdater } from '../lib/vision/updater';

// TODO: BE API to get free port
const DEFAULT_PORT = 8000;

const useStore = defineStore(
  'vision',
  () => {
    console.debug('vision.store');

    const isEnabled = ref(false);

    const updater = new VisionUpdater(deps);
    const visionDir = ref<string>();
    const version = ref<string>();
    const isInstalled = computed(() => !!visionDir.value);
    const isInstalling = ref(false);

    const runner = new VisionRunner(deps);
    const pid = ref<number>();
    const isRunning = computed(() => !!pid.value);

    const init = async () => {
      try {
        if (!isEnabled.value) {
          return;
        }

        const { needsUpdate, installedManifest, latestManifest } =
          await updater.checkForUpdates(visionDir.value);

        if (installedManifest) {
          version.value = installedManifest.version;
        }

        if (needsUpdate && latestManifest) {
          isInstalling.value = true;
          const { pathJoin } = deps;
          const url = new URL(latestManifest.url);
          url.searchParams.set('t', latestManifest.checksum);
          const downloaded = await Bindings.fs.downloadZip(url.toString());
          if (!downloaded[0]?.path) throw new Error('Error downloading plugin');
          const dir = dirname(downloaded[0].path);

          if (visionDir.value) {
            /*
             * Decided to keep this logic here, in case removing an old version
             * ever differs from uninstall
             */
            await stop();
            console.debug('removing dir', visionDir.value);
            await Bindings.fs.remove(visionDir.value, true, true);
          }

          const relativeDir = basename(dir) as string;

          await Bindings.fs.writeFile(
            await pathJoin(relativeDir, 'manifest.json'),
            JSON.stringify(latestManifest),
            false,
          );

          visionDir.value = dir;
          version.value = latestManifest.version;
          isInstalling.value = false;

          console.debug(
            `downloaded Vision ${latestManifest.version} to ${visionDir.value}`,
          );
        }

        await start();
        subscribeToEvents();
      } catch (e) {
        console.error('Error initializing Vision', e);
        throw e;
      } finally {
        isInstalling.value = false;
      }
    };

    const toggleEnable = (shouldEnable: boolean) => {
      isEnabled.value = shouldEnable;

      const shouldInstall = shouldEnable && !isInstalled.value;
      const shouldStopRunningProcess = !shouldEnable && isRunning.value;
      const shouldStart = shouldEnable && isInstalled.value && !isRunning.value;

      if (shouldInstall || shouldStart) {
        return init();
      } else if (shouldStopRunningProcess) {
        return stop();
      }
    };

    const start = async () => {
      if (visionDir.value && !pid.value) {
        const dir = basename(visionDir.value);
        if (dir) {
          pid.value = await runner.start(dir, { hidden: true });
        }
      }
    };

    const stop = async () => {
      if (!pid.value) {
        return;
      }
      await runner.stop(pid.value);
      pid.value = undefined;
    };

    const uninstall = async () => {
      if (visionDir.value) {
        await stop();
        console.debug('removing dir', visionDir.value);
        await Bindings.fs.remove(visionDir.value, true, true);
        // TODO: dependent coupled state, reshape
        visionDir.value = undefined;
        version.value = undefined;
      }
    };

    const eventSource = ref<EventSource>();

    const subscribeToEvents = (port = DEFAULT_PORT) => {
      eventSource.value?.close();

      const es = new EventSource(`http://localhost:${port}/events`);
      eventSource.value = es;

      // These just log on SL Desktop. I think it's unnecessary noise
      es.onopen = () => {};
      es.onerror = () => {};

      es.onmessage = (e) => {
        try {
          const parsed = JSON.parse(e.data);

          if (
            Array.isArray(parsed.events) &&
            hasGameDetectedEvent(parsed.events)
          ) {
            return;
          }

          parsed.vision_event_id = uuidv4();

          forwardEventToApi(parsed as ParsedEvent);
        } catch (e) {
          console.error('Error processing Vision event', e);
        }
      };

      const auth = useAuthStore();
      auth.socket.on('visionEvent', forwardToSources);
      auth.socket.on('userStateUpdated', forwardToSources);
    };

    type ForwardVisionWSEvent =
      | StreamlabsSocketEvents['visionEvent']
      | StreamlabsSocketEvents['userStateUpdated'];

    const forwardToSources = (e: ForwardVisionWSEvent) => {
      const obs = useObsStore();

      return Promise.all(
        obs.reactiveSources.map((s) => {
          // console.debug('Forwarding event', e, 'to', s.name);
          return Bindings.obs.browsersource_sendMessage(
            s.name,
            // Note that the backend will wrap this in a { message: e } shape
            JSON.stringify(e),
          );
        }),
      );
    };

    type VisionEvent = GameProcessDetectedEvent;
    type GameProcessDetectedEvent = {
      name: 'game_process_detected';
    };
    type Uuid = string;
    type ParsedEvent = {
      vision_event_id: Uuid;
      events: VisionEvent[];
    };

    const hasGameDetectedEvent = (events: VisionEvent[]) => {
      return events.some((e) => e.name === 'game_process_detected');
    };

    const api = useApiStore();

    const forwardEventToApi = async (event: ParsedEvent) => {
      api.post('/api/v5/vision/desktop/event', event, { timeout: 8_000 });
    };

    window.addEventListener('beforeunload', () => stop());

    return {
      init,
      isEnabled,
      isInstalled,
      isInstalling,
      isRunning,
      toggleEnable,
      version,
      visionDir,
      stop,
      uninstall,
    };
  },
  {
    persist: {
      pick: ['isEnabled', 'visionDir', 'version'],
      storage: piniaPluginPersistedstate.localStorage(),
    },
  },
);

const dirname = (path: string) => {
  return path.match(/(.*)[/\\]/)?.[1] || '';
};

const basename = (path: string) => {
  return path.split(/[/\\]/).pop();
};

export const useVisionStore = useStore;

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useStore, import.meta.hot));
}
